import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { carHireColumns, carHireGrandTotalFields, carHireTotalColumns, distributionGrandTotalFields, distributionTotalColumns, farmDcDistributionColumns, flightsAndAccommodationColumns, flightsAndAccommodationGrandTotalFields, flightsAndAccommodationTotalColumns, interDcDistributionColumns, petrolCardsColumns, petrolCardsGrandTotalFields, petrolCardsTotalColumns, regionalDistributionColumns, shippingDistributionColumns } from '../constants/category-three-columns';
import { getPetrolCardHolders, getPetrolCardsData, getPetrolCardsDataTotals, postPetrolCardsData } from '../../../../../actions/carbonfootprint-rework/03-indirect-ghg-emissions-from-transportation/03-petrol-card-holder-actions';
import { getFlightsAndAccommodationData, getFlightsAndAccommodationDataTotals, postFlightsAndAccommodationData } from '../../../../../actions/carbonfootprint-rework/03-indirect-ghg-emissions-from-transportation/01-flights-and-accommodation-actions';
import { getCarHireData, getCarHireDataTotals, postCarHireData } from '../../../../../actions/carbonfootprint-rework/03-indirect-ghg-emissions-from-transportation/02-car-hire-actions';
import { getDownstreamDistributionData, getDownstreamDistributionDataTotals, postDownstreamDistributionData } from '../../../../../actions/carbonfootprint-rework/03-indirect-ghg-emissions-from-transportation/04-downstream-distribution-actions';
import useTsPaginator from 'ts-paginator';
import { MONTHMAPPER } from '../constants/months';
import { uploadCsvDocument } from '../../../../../actions/carbonfootprint-rework/00-base/00-base-actions';


export function useCategoryThreeDataStore(selectedCategory, year, company, group, facility) {
    const dispatch = useDispatch();
    const [month, setMonth] = useState(0);

    // --- declare useSelector variables ---
    const flightsAndAccommodationData = useSelector((state) => state.flightsAndAccommodationReducer.flightsAndAccommodationData?.data)
    const flightsAndAccommodationTableRecords = useSelector((state) => state.flightsAndAccommodationReducer.flightsAndAccommodationData?.pagination?.total_records)
    const flightsAndAccommodationDataTotals = useSelector((state) => state.flightsAndAccommodationReducer.flightsAndAccommodationDataTotals?.data)
    const carHireData = useSelector((state) => state.carHireReducer.carHireData?.data)
    const carHireTableRecords = useSelector((state) => state.carHireReducer.carHireData?.pagination?.total_records)
    const carHireDataTotals = useSelector((state) => state.carHireReducer.carHireDataTotals?.data)
    const petrolCardsData = useSelector((state) => state.petrolCardHolderReducer.petrolCardData?.data)
    const petrolCardsTableRecords = useSelector((state) => state.petrolCardHolderReducer.petrolCardData?.pagination?.total_records)
    const petrolCardsDataTotals = useSelector((state) => state.petrolCardHolderReducer.petrolCardDataTotals?.data)
    const { petrolCardHolders } = useSelector(state => state.petrolCardHolderReducer)
    const downstreamDistributionData = useSelector((state) => state.downstreamDistributionReducer.downstreamDistributionData?.data)
    const downstreamDistributionTableRecords = useSelector((state) => state.downstreamDistributionReducer.downstreamDistributionData?.pagination?.total_records)
    const downstreamDistributionDataTotals = useSelector((state) => state.downstreamDistributionReducer.downstreamDistributionDataTotals?.data)

    // --- variable manipulation, to make hook call cleaner ---

    const distributionCategories = [
        'Inter-DC Distribution',
        'Farm-DC Distribution',
        'Shipping Distribution',
        'Regional Distribution'
    ];

    const selectedCategoryDataMap = {
        'Flights and Accommodation': {
            data: flightsAndAccommodationData,
            totals: flightsAndAccommodationDataTotals,
            getDataAction: getFlightsAndAccommodationData,
            getTotalsAction: getFlightsAndAccommodationDataTotals,
            postDataAction: postFlightsAndAccommodationData,
            categoryKey: 'flights-and-accommodation',
            columns: flightsAndAccommodationColumns,
            totalColumns: flightsAndAccommodationTotalColumns,
            grandTotalFields: flightsAndAccommodationGrandTotalFields,
            tableRecordCount: flightsAndAccommodationTableRecords,
        },
        'Car Hire': {
            data: carHireData,
            totals: carHireDataTotals,
            getDataAction: getCarHireData,
            getTotalsAction: getCarHireDataTotals,
            postDataAction: postCarHireData,
            categoryKey: 'car-hire',
            columns: carHireColumns,
            totalColumns: carHireTotalColumns,
            grandTotalFields: carHireGrandTotalFields,
            tableRecordCount: carHireTableRecords,
        },
        'Petrol Cards': {
            data: petrolCardsData,
            totals: petrolCardsDataTotals,
            getDataAction: getPetrolCardsData,
            getTotalsAction: getPetrolCardsDataTotals,
            postDataAction: postPetrolCardsData,
            categoryKey: 'petrol-card',
            columns: petrolCardsColumns(petrolCardHolders.data),
            totalColumns: petrolCardsTotalColumns,
            grandTotalFields: petrolCardsGrandTotalFields,
            generalInformationData: petrolCardHolders.data,
            tableRecordCount: petrolCardsTableRecords,
        },
        'Inter-DC Distribution': {
            data: downstreamDistributionData,
            totals: downstreamDistributionDataTotals,
            getDataAction: getDownstreamDistributionData,
            getTotalsAction: getDownstreamDistributionDataTotals,
            postDataAction: postDownstreamDistributionData,
            categoryKey: 'downstream-distribution',
            columns: interDcDistributionColumns,
            distributionType: selectedCategory,
            totalColumns: distributionTotalColumns,
            grandTotalFields: distributionGrandTotalFields,
            tableRecordCount: downstreamDistributionTableRecords,
        },
        'Farm-DC Distribution': {
            data: downstreamDistributionData,
            totals: downstreamDistributionDataTotals,
            getDataAction: getDownstreamDistributionData,
            getTotalsAction: getDownstreamDistributionDataTotals,
            postDataAction: postDownstreamDistributionData,
            categoryKey: 'downstream-distribution',
            columns: farmDcDistributionColumns,
            distributionType: selectedCategory,
            totalColumns: distributionTotalColumns,
            grandTotalFields: distributionGrandTotalFields,
            tableRecordCount: downstreamDistributionTableRecords,
        },
        'Shipping Distribution': {
            data: downstreamDistributionData,
            totals: downstreamDistributionDataTotals,
            getDataAction: getDownstreamDistributionData,
            getTotalsAction: getDownstreamDistributionDataTotals,
            postDataAction: postDownstreamDistributionData,
            categoryKey: 'downstream-distribution',
            columns: shippingDistributionColumns,
            distributionType: selectedCategory,
            totalColumns: distributionTotalColumns,
            grandTotalFields: distributionGrandTotalFields,
            tableRecordCount: downstreamDistributionTableRecords,
        },
        'Regional Distribution': {
            data: downstreamDistributionData,
            totals: downstreamDistributionDataTotals,
            getDataAction: getDownstreamDistributionData,
            getTotalsAction: getDownstreamDistributionDataTotals,
            postDataAction: postDownstreamDistributionData,
            categoryKey: 'downstream-distribution',
            columns: regionalDistributionColumns,
            distributionType: selectedCategory,
            totalColumns: distributionTotalColumns,
            grandTotalFields: distributionGrandTotalFields,
            tableRecordCount: downstreamDistributionTableRecords,
        },
    }

    const {
        data,
        totals,
        getDataAction,
        getTotalsAction,
        postDataAction,
        categoryKey,
        distributionType,
        columns,
        totalColumns,
        grandTotalFields,
        generalInformationData,
        tableRecordCount
    } = selectedCategoryDataMap[selectedCategory] ||
        {
            data: null,
            totals: null,
            getDataAction: null,
            getTotalsAction: null,
            postDataAction: null,
            categoryKey: null,
            distributionType: null,
            columns: [],
            totalColumns: [],
            grandTotalFields: [],
            generalInformationData: [],
            tableRecordCount: 0,
        }

    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    useEffect(() => {
        // if (currentProjectId !== projectId) {
        _handleChangePage(0); // Reset to the first page
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount); // Reset to the first page
        // }
    }, [tableRecordCount]);

    useEffect(() => {
        const monthForBackend = MONTHMAPPER[month];
        if (selectedCategory === 'Flights and Accommodation') {
            dispatch(getFlightsAndAccommodationData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }
        if (selectedCategory === 'Car Hire') {
            dispatch(getCarHireData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }
        if (selectedCategory === 'Petrol Cards') {
            dispatch(getPetrolCardHolders(group))
            dispatch(getPetrolCardsData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }
        if (distributionCategories.includes(selectedCategory)) {
            dispatch(getDownstreamDistributionData(year, facility, selectedCategory, rowsPerPage, currentPage, monthForBackend))
        }

    }, [dispatch, selectedCategory, year, facility, group, company, rowsPerPage, currentPage]
    )

    return {
        month,
        setMonth,
        data,
        totals,
        getDataAction,
        getTotalsAction,
        postDataAction,
        uploadCsvDocument,
        categoryKey,
        distributionType,
        columns,
        totalColumns,
        grandTotalFields,
        year,
        facility,
        group,
        company,
        generalInformationData,
        // pagination
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    }
}